export const Collection = {
  ANNOUNCEMENTS: 'announcements',
  ATTENDEES: 'attendees',
  ATTENDEE_DEVICE: 'attendees-device',
  CHATS: 'chats',
  CONFERENCES: 'conference',
  CONFIGURATIONS: 'configuration',
  EVENT_CARD_STYLES: 'event-card-styles',
  EVENTS: 'events',
  SESSIONS: 'sessions',
  SPEAKERS: 'speakers',
  SPONSORS: 'sponsors',
  TALKS: 'talks',
  BUILDINGS: 'venues',
  MODERATORS: 'moderators',
  VENDORS: 'vendors',
  SPONSORSHIP_LEVELS: 'sponsorship-levels',
  USERS: 'users',
  SUBJECTS: 'subjects',
  POLLS: 'polls',
  QUESTIONS: 'questions',
  ANSWERS: 'poll-answers',
  SETTINGS: 'settings',
  ATTENDANCE: 'attendance',
  BADGE_CONFIGURATIONS: 'badge-configurations',
  BADGE_SUBJECTS: 'badge-subjects',
  ATTACHMENTS: 'attachments',
  ORGANIZATIONS: 'organizations',
  COMMUNITY_POSTS: 'community-posts',
  POST_TOPICS: 'post-topics',
  FLAGGED_POSTS: 'post-flagged',
  NOTIFICATION_LOGS: 'notification-logs'
}

// Known push-notification types written by the Cloud Functions backend.
// `value` MUST match the exact `notificationType` string stored in Firestore
// (the backend writes hyphenated lowercase, e.g. "chat-message"). `label` is
// for display. The list view also unions in any types discovered in loaded
// logs, so types not enumerated here still appear in the filter once seen.
export const NOTIFICATION_TYPES = [
  { value: 'announcement', label: 'Announcement' },
  { value: 'chat-message', label: 'Chat Message' },
  { value: 'group-chat-message', label: 'Group Chat Message' },
  { value: 'post-created', label: 'Post Created' },
  { value: 'post-commented', label: 'Post Commented' },
  { value: 'post-liked', label: 'Post Liked' },
  { value: 'post-unliked', label: 'Post Unliked' },
  { value: 'post-flagged', label: 'Post Flagged' },
  { value: 'post-pinned', label: 'Post Pinned' },
  { value: 'post-unpinned', label: 'Post Unpinned' },
  { value: 'favorite-reminder', label: 'Favorite Reminder' },
  { value: 'comment-liked', label: 'Comment Liked' }
  // QA notifications are not yet in place — uncomment when the backend ships them.
  // { value: 'qa-new-question', label: 'New Question' },
  // { value: 'qa-question-reply', label: 'Question Reply' }
]

export const Roles = {
  ADMIN: 'admin',
  SUPER_ADMIN: 'super-admin',
  ACCOUNT_OWNER: 'owner',
  ORGANIZER: 'organizer'
}

export const SpeakerRoles = {
  PRESENTER: 'presenter',
  MODERATOR: 'moderator'
}

export const AttendeeRoles = {
  ATTENDEE: 'Attendee',
  SPEAKER: 'Speaker',
  MODERATOR: 'Moderator',
  REPRESENTATIVE: 'Organization Representative'
}

export const AnnouncementStatus = Object.freeze({
  SCHEDULED: 'scheduled',
  SENT: 'sent'
})

export const EventTypes = Object.freeze({
  STANDALONE: 'standalone',
  HAVE_TALKS: 'haveTalks',
  HAVE_SESSIONS: 'haveSessions'
})

export const PollStatus = Object.freeze({
  CREATED: 'created',
  STARTED: 'started',
  PAUSED: 'paused',
  RESUMED: 'resumed',
  CLOSED: 'closed'
})

export const Letters = [
  'A',
  'B',
  'C',
  'D',
  'E',
  'F',
  'G',
  'H',
  'I',
  'J',
  'K',
  'L',
  'M',
  'N',
  'O',
  'P',
  'Q',
  'R',
  'S',
  'T',
  'U',
  'V',
  'W',
  'X',
  'Y',
  'Z'
]

export const SubjectType = Object.freeze({
  ATTENDEE: 'Attendee',
  EXHIBITOR: 'Exhibitor',
  SPEAKER: 'Speaker',
  SPONSOR: 'Sponsor'
})

export const SubjectStatus = Object.freeze({
  INVITE_NOT_SENT: 'Invite not sent',
  AWAITING_RESPONSE: 'Awaiting response',
  PENDING_DATA_REVIEW: 'Pending review',
  APPROVED: 'Approved'
})

export const organizationStatus = Object.freeze({
  INCOMPLETE: 'Incomplete',
  INVITE_NOT_SENT: 'Invite not sent',
  AWAITING_RESPONSE: 'Awaiting response',
  PENDING_DATA_REVIEW: 'Pending review',
  APPROVED: 'Approved'
})

export const creationType = Object.freeze({
  INVITATION: 'invitation',
  MANUAL: 'manual'
})

export const orgRepresentativeStatus = Object.freeze({
  NOTIFIED: 'notified',
  NOT_NOTIFIED: 'not-notified',
  REGISTERED: 'registered'
})

export const MeetCSympleTeamEmail = 'hamed.amani@csymplicity.com'

export const SubjectInfoMessages = Object.freeze({
  SPEAKER:
    'We are delighted to extend an invitation to you to join us as a speaker at our upcoming conference?. Your expertise and insights would greatly enrich the event and contribute to its success.',
  EXHIBITOR:
    'We are delighted to extend an invitation to you to join us as an Exhibitor at our upcoming conference?. You will have the opportunity to showcase your products and services to a diverse audience of professionals, researchers, and enthusiasts. Your participation will not only provide valuable exposure for your brand but also contribute to the overall success of the event. Join us to connect with potential clients, build partnerships, and stay at the forefront of industry advancements.',
  SPONSOR:
    'We are delighted to extend an invitation to you to join us as an Organization at our upcoming conference?. As an organization, you will have the opportunity to showcase your brand, demonstrate your commitment to the industry, and reach a diverse audience of professionals, researchers, and enthusiasts. Your sponsorship will not only support the success of the event but also provide valuable exposure and networking opportunities for your organization.'
})

export const SPONSOR_INFO_TEXT = Object.freeze({
  organization: 'Name of your Organization',
  websiteURL: 'URL of your website',
  bannerClickURL:
    'Enter the destination URL for the banner click. This is the web page or destination where users will be redirected when they click on the banner within the mobile app.',
  introduction:
    "Provide a detailed introduction to your organization, highlighting your products, services, and key achievements. This information will be displayed on the organization's page in the mobile app, giving event participants a comprehensive understanding of your organization.",
  message:
    'Enter a catchy and concise message about your organization and sponsorship. This message popup in mobile app as participants click your logo displayed with events sponsored by your organization, capturing the attention of event participants.',
  logo: 'Upload your organization logo here. This logo will be displayed in our mobile app when users view your sponsorship.',
  adBanner:
    'Upload your banner image here. This banner will be displayed in our mobile app on the event details page sponsored by your organization.',
  brochure:
    'Upload your brochure image here. This brochure will be available for users to view when they visit your organization introduction page in our mobile app.'
})

export const SPEAKER_INFO_TEXT = Object.freeze({
  bio: 'Provide a brief biography about yourself. This will be displayed in our mobile app when users view your speaker profile, allowing them to learn more about your background and expertise.',
  profile:
    'Upload your professional headshot here. This photo will be displayed in your speaker profile and alongside the talks you deliver in our mobile app.',
  roles:
    'Select one or more roles for this speaker. A speaker can be a Presenter, a Moderator, or both.'
})

export const POLLING_END_USER_MSGS = Object.freeze({
  inActive: {
    header: 'Waiting for poll to Active',
    subText: 'As soon as the poll is active, you will see it on the screen here.'
  },
  toBegin: {
    header: 'Waiting for poll to begin',
    subText:
      'Your poll is underway. As soon as the poll begins, you will see it on the screen here.'
  },
  toResume: {
    header: 'Waiting for poll to resume',
    subText: 'As soon as the poll resumes, you will see it on the screen here.'
  },
  closed: {
    header: 'This poll has been concluded',
    subText: 'We appreciate your participation. Thank you!'
  }
})

export const AssociatedPollMsg =
  'This poll has already been associated with another event, session or talk.'

export const subjectReason = {
  registration: 'registration',
  forApproval: 'for_approval',
  afterApproval: 'after_approval'
}

// Prefix that marks a QR code as belonging to this app, so the mobile scanner can
// reject any QR that is not ours. Full payload: `::MS::ConfCode:{confCode}`
export const attendanceQrPrefix = '::MS::ConfCode:'

export const badgeConfigurationDefaultFields = [
  { key: 'firstName', value: 'First Name' },
  { key: 'lastName', value: 'Last Name' },
  { key: 'credential', value: 'Credential' },
  { key: 'position', value: 'Position' },
  { key: 'organization', value: 'Organization' },
  { key: 'city', value: 'City' },
  { key: 'state', value: 'State' },
  { key: 'country', value: 'Country' },
  { key: 'email', value: 'Email' },
  { key: 'phone', value: 'Phone' }
]

export const possibleBadgeConfigurationFields = [
  { key: 'firstName', value: 'First Name' },
  { key: 'lastName', value: 'Last Name' },
  { key: 'credential', value: 'Credential' },
  { key: 'position', value: 'Position' },
  { key: 'organization', value: 'Organization' },
  { key: 'city', value: 'City' },
  { key: 'state', value: 'State' },
  { key: 'country', value: 'Country' }
]

export const badgesSubjectsApplication = [
  'Attendee',
  'Speaker',
  'Moderator',
  'Exhibitor',
  'Sponsor'
]
